import { getAccessToken, logout } from './auth';
import type { InstantClosingResult, ServicepartnerOption } from './stockDevices';

// Die Auswahlmenge fuer den geplanten Versand hat dieselbe Form wie im Geraetelager
// (derselbe Server-Helper) - hier weitergegeben, damit die Gerätewahl nur einen
// Service importieren muss.
export type { ErrorPatternOption, ServicepartnerOption } from './stockDevices';

export interface TicketListItem {
  id:                 number;
  order_number:       string;
  type:               string;
  status:             string;
  status_id:          number;
  status_bg:          string;
  status_fg:          string;
  /** Zeitpunkt des letzten Statuswechsels; null bei Altfällen ohne rekonstruierbaren Zeitpunkt. */
  status_changed_timestamp: string | null;
  is_closed:          boolean;
  is_billed:          boolean;
  billing_note:       string | null;
  assona_damage_nr:   string;
  assona_contract_nr: string;
  /** Assona-Reklamation (Folgefall eines früheren 24h-Austauschs). */
  is_complaint:       boolean;
  imei_sn:            string;
  creation_timestamp: string;
}

/** Ein Assona-Task mit seinen R2-Tickets (leer = noch kein R2-Fall). */
export interface AssonaTaskRef {
  task_id:    number;
  ticket_ids: number[];
}

/** Filter der Ticketliste. Alle optional und kombinierbar. */
export interface TicketListFilter {
  statusId?: number;
  /** true = nur verrechnete, false = nur unverrechnete, undefined = alle */
  billed?:   boolean;
  /** Suchbegriff über Ticketnummer, Schadens-/Vertragsnummer und IMEI/SN. */
  q?:        string;
  /** Seite ab 1 (Server-Default 1). */
  page?:     number;
  /** Einträge je Seite, höchstens `R2TicketListHandler::LIMIT` (Server-Default = LIMIT). */
  perPage?:  number;
}

/** Anzahl je Status über alle Assona24H-Tickets — vom Server gezählt, nicht aus der Seite. */
export interface TicketListStatusCount {
  status_id: number;
  status:    string;
  count:     number;
  status_bg: string;
  status_fg: string;
}

/** Eine Seite der Ticketliste. */
export interface TicketListPage {
  items:         TicketListItem[];
  /** Treffer mit den gesetzten Filtern, über alle Seiten. */
  total:         number;
  /** Anzahl je Status über alle Assona24H-Tickets, unabhängig von Status-Filter und Suche. */
  status_counts: TicketListStatusCount[];
}

/**
 * Zustand der Assona-Meldung zum jüngsten Reparaturbericht.
 * Wird serverseitig aus der Übertragungs-Queue abgeleitet.
 */
export type AssonaTransmissionState = 'none' | 'pending' | 'sent' | 'failed';

/** Um welche Meldung es geht — ohne das las sich jede Meldung wie ein Reparaturbericht. */
export type AssonaTransmissionKind = 'device_received' | 'rep_report' | 'total_loss';

export interface AssonaTransmission {
  state:          AssonaTransmissionState;
  kind:           AssonaTransmissionKind | null;
  /** frühester Sendezeitpunkt (nur bei 'pending') */
  due_at:         string | null;
  transmitted_at: string | null;
  error:          string | null;
}

/** Schritte der Lagerbuchung zum Austauschgeraet (Schluessel wie in R2SwapStockBooking). */
export const STEP_CLOSE_DELIVERY = 'lieferschein_abschliessen';
export const STEP_BOOK_OUT       = 'geraet_ausbuchen';

/**
 * Stand der beiden Lagerbuchungen zum Austauschgeraet. `open_steps` sagt, was noch
 * offen ist — leer heisst: nichts zu tun, dann wird auch nicht gefragt.
 */
export interface SwapStockState {
  actionable:           boolean;
  delivery_closed:      boolean;
  device_booked_out:    boolean;
  open_steps:           string[];
  delivery_slip_number: string | null;
  device_description:   string | null;
  imei:                 string | null;
  damage_nr:            string;
  notices:              string[];
}

/** Ergebnis eines Buchungslaufs. `failed` benennt den Schritt, der nicht ging. */
export interface SwapStockBookingResult {
  performed: string[];
  skipped:   string[];
  failed:    string | null;
  error:     string | null;
}

export interface TicketBilling {
  is_billed:      boolean;
  billed_at:      string | null;
  invoice_number: string | null;
}

export type FmipStatus = 'active' | 'inactive' | 'unknown';

export interface DeviceRef {
  id:          number;
  description: string;
}

export interface ProcessRef {
  key:   string;
  label: string;
}

/**
 * Schlüssel der serverseitig angebotenen Prozesse. Welche davon verfügbar sind,
 * entscheidet AUSSCHLIESSLICH der Server über `available_processes` — im Frontend
 * darf keine Statuslogik nachgebaut werden (sonst driftet sie auseinander).
 */
export const PROCESS_EINGELANGT        = 'eingelangt';
export const PROCESS_KOSTENVORANSCHLAG = 'kostenvoranschlag';
export const PROCESS_REPARATURBERICHT  = 'reparaturbericht';
/** Assona24H: KV und Reparaturbericht in einem Schritt. */
export const PROCESS_KV_REPBERICHT     = 'kv_repbericht';
export const PROCESS_ABSCHLIESSEN      = 'abschliessen';

/** Bietet der Server diesen Prozess für das Ticket an? */
export function hasProcess(ticket: { available_processes: ProcessRef[] }, key: string): boolean {
  return ticket.available_processes.some(p => p.key === key);
}

export interface TicketHistoryEntry {
  id:                 number;
  note:               string;
  json_data:          Record<string, unknown> | null;
  user_id:            number;
  user_name:          string;
  creation_timestamp: string;
}

export type FileCategory = 'artikelfoto' | 'rechnung' | 'anderes';

export interface TicketFile {
  id:          number;
  description: string;
  category:    FileCategory | string;
  uploaded_at: string;
  user_name:   string;
}

export interface TicketDetail {
  id:                          number;
  order_number:                string;
  type:                        string;
  status:                      string;
  status_bg:                   string;
  status_fg:                   string;
  /** Zeitpunkt des letzten Statuswechsels; null bei Altfällen ohne rekonstruierbaren Zeitpunkt. */
  status_changed_timestamp:    string | null;
  creation_timestamp:          string;
  device:                      DeviceRef | null;
  /** Altgerät ohne Master: die händische Bezeichnung steht an der Stelle des Geräts. */
  alternative_device_description: string | null;
  is_legacy_device:            boolean;
  device_color:                DeviceColorRef | null;
  device_condition:            DeviceConditionRef | null;
  /** Vorgemerkter Versand an einen Servicepartner; wird beim Abschluss ausgeführt. */
  planned_shipment:            PlannedShipmentRef | null;
  /** Lagergerät zu diesem Ticket, sobald eingebucht. */
  stock_device:                StockDeviceRef | null;
  /** Abgeschlossen, aber noch nicht im Lager — Bestandsticket zum Nachziehen. */
  can_create_stock_device:     boolean;
  device_memory_size:          number | null;
  /** Speichergröße als Anzeigetext, z.B. „128 GB". */
  device_memory_size_label:    string | null;
  damage_description:          string;
  original_device_description: string | null;
  customer_input_description:  string | null;
  assona_damage_nr:            string;
  assona_contract_nr:          string;
  task_id:                     number | null;
  /** Alle Tasks zur Schadensnummer. Mehr als einer = moeglicher Sonderfall (Reklamation). */
  assona_task_ids:             number[];
  /** Assona-Serviceordertyp (1 = 24h Austausch, 2 = Reklamation, 3 = Diebstahl), null = keiner. */
  service_order_type:          { id: number; description: string } | null;
  is_complaint:                boolean;
  /** Nur bei Reklamation: Original-Austausch; null = unbekannt bzw. keine Reklamation. */
  complaint_origin:            AssonaTaskRef | null;
  /** Am Original: Reklamationen, deren Originalfall dieser Task ist. */
  complaints:                  AssonaTaskRef[];
  imei_sn:                     string;
  fmip_status:                 FmipStatus;
  fmip_checked_at:             string | null;
  /** Gerätebezeichnung laut IMEI-Prüfung (imei_informations), null wenn nie geprüft. */
  imei_check_device:           string | null;
  /** true, wenn zur IMEI eine HTML-Auskunft der Schnittstelle vorliegt (Service 205/190). */
  has_imei_information:        boolean;
  imei_verified:               boolean;
  imei_not_verified_reason:    string;
  battery_test_capacity:       number | null;
  battery_test_reject_reasons: string | null;
  status_id:                   number;
  is_closed:                   boolean;
  closed_at:                   string | null;
  closed_by:                   string | null;
  billing:                     TicketBilling;
  assona_transmission:         AssonaTransmission;
  swap_stock:                  SwapStockState;
  available_processes:         ProcessRef[];
  files:                       TicketFile[];
  history:                     TicketHistoryEntry[];
}

export interface ReceivedDeviceVendor {
  id:          number;
  description: string;
}

/** Ein für Reparatur freigegebener Masterartikel — Auswahlmenge für das eingegangene Gerät. */
export interface DeviceColorRef {
  id:          number;
  description: string;
}

/** Lagergerät (handyb_r2.devices), Anzeige „R2-<repair_ticket_id>". */
export interface StockDeviceRef {
  id:           number;
  stock_number: string;
}

/** Gerätezustand („Grading") aus handyb_shop.device_condition. */
export interface DeviceConditionRef {
  id:          number;
  description: string;
}

/**
 * Vorauswahl für den Zustand in „Eingegangenes Gerät wählen": „Gebraucht D
 * (Akzeptabel/Defekt)" — der Normalfall für ein Reparaturgerät.
 * Feste ID aus handyb_shop.device_condition, wie R2DeviceConditions::SELECTABLE_IDS.
 */
export const DEFAULT_CONDITION_ID = 11;

/**
 * Der am Ticket vorgemerkte Versand an einen Servicepartner. `price_label` ist der
 * Preis des Fehlerbilds für diesen Master bei diesem Partner, schon formatiert.
 */
export interface PlannedShipmentRef {
  servicepartner: DeviceRef;
  error_pattern:  DeviceRef;
  price_net_eur:  number | null;
  price_label:    string;
  /** true = händischer Preis (Altgerät), sonst Preis der Rezeptur. */
  is_manual_price: boolean;
}

/**
 * Ergebnis des automatisch mit dem Lagergerät ausgeführten Versands.
 *
 * `attempted` false = am Ticket war keiner vorgemerkt.
 * `created` true, `printed` false = Versand steht, nur das Etikett fehlt — es liegt
 * in der Druckwarteschlange und wird dort nachgedruckt.
 * `created` false = es entstand kein Versand; der muss im Gerätelager wiederholt werden.
 */
export interface PlannedShipmentResult {
  attempted: boolean;
  created:   boolean;
  printed:   boolean;
  message:   string | null;
  /** Sofortiger LS-Abschluss beim internen Servicepartner; null, wenn kein Versand entstand. */
  instant_closing?: InstantClosingResult | null;
}

/** Abschluss bzw. nachträgliche Einbuchung: frisches Ticket + was der Versand getan hat. */
export interface StockBookingResult {
  ticket:           TicketDetail;
  planned_shipment: PlannedShipmentResult;
}

export interface DeviceMemorySize {
  memory_size: number;
  /** Anzeigetext, z.B. „128 GB". */
  description: string;
}

/** Eine Farbe des Masters samt der dafür angelegten Speichergrößen. */
export interface DeviceColorVariant {
  id:           number;
  description:  string;
  memory_sizes: DeviceMemorySize[];
}

export interface DeviceVariants {
  colors: DeviceColorVariant[];
  /** true = zum Master gibt es keinen Artikel mit Provider „Frei", Liste stammt aus allen Providern. */
  provider_fallback: boolean;
}

export interface ReceivedDeviceResult {
  id:          number;
  description: string;
  vendor:      ReceivedDeviceVendor;
}

export interface EingelangtPayload {
  imei_verified?:               boolean;
  imei_not_verified_reason?:    string;
  battery_test_capacity?:       number;
  battery_test_reject_reasons?: string;
}

/**
 * Antwort des Wareneingangs. Die Lagerbuchungen zum Austauschgeraet gehoeren
 * NICHT mehr hierher — sie laufen beim Erstellen von KV/Reparaturbericht mit
 * (siehe `swapDocuments.ts`).
 */
export interface EingelangtResult {
  ticket: TicketDetail;
}

export interface FmipResult {
  fmip_status:       FmipStatus;
  fmip_checked_at:   string | null;
  imei_check_device: string | null;
}

/**
 * Gespeicherte HTML-Auskunft der IMEI-Schnittstelle. `html` stammt vom
 * Drittanbieter und wird deshalb nur in einem abgeschotteten iframe angezeigt.
 */
export interface ImeiInformation {
  html:       string;
  service_id: number;
  checked_at: string;
}

export interface TicketStatusCount {
  status:    string;
  count:     number;
  status_bg: string;
  status_fg: string;
}

export interface TicketStats {
  total:         number;
  created_today: number;
  by_status:     TicketStatusCount[];
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
  /** Nur bei der Ticketliste. */
  total?:         number;
  status_counts?: TicketListStatusCount[];
  /** Nur bei /masterarticles/{id}/variants. */
  provider_fallback?: boolean;
  /** Nur beim Abschluss und der nachträglichen Einbuchung. */
  planned_shipment?: PlannedShipmentResult;
  /** Nur bei „Reparatur hinzufügen" (`ticket_already_exists`): die vorhandenen Tickets. */
  ticket_ids?: number[];
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
    /** Ganze Antwort, für Fehler mit Zusatzangaben (z. B. `ticket_ids`). */
    readonly body?: ApiEnvelope<unknown>,
  ) {
    super(message);
  }
}

const BASE         = `${window.location.origin}/api/tickets`;
const DEVICES_BASE = `${window.location.origin}/api/devices`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
      body,
    );
  }

  return body;
}

export async function list(filter: TicketListFilter = {}): Promise<TicketListPage> {
  const params = new URLSearchParams();
  if (filter.statusId !== undefined) params.set('status_id', String(filter.statusId));
  if (filter.billed   !== undefined) params.set('billed', filter.billed ? '1' : '0');
  if (filter.q        !== undefined && filter.q !== '') params.set('q', filter.q);
  if (filter.page     !== undefined) params.set('page', String(filter.page));
  if (filter.perPage  !== undefined) params.set('per_page', String(filter.perPage));
  const query = params.toString();
  const body = await request<TicketListItem>(query ? `${BASE}?${query}` : BASE, { method: 'GET' });
  return {
    items:         body.items ?? [],
    total:         body.total ?? 0,
    status_counts: body.status_counts ?? [],
  };
}

export async function get(id: number): Promise<TicketDetail> {
  const body = await request<TicketDetail>(`${BASE}/${id}`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Ein Fall (= Assona-Task) zu einer Schadensnummer, Auswahl bei „Reparatur hinzufügen". */
export interface Assona24hCase {
  task_id:            number;
  /** „24h Austausch", „Reklamation", „Diebstahl" */
  service_order_type: string;
  task_created:       string | null;
  device:             string | null;
  /** Vorhandene R2-Tickets zu diesem Task. */
  ticket_ids:         number[];
  creatable:          boolean;
  blocked_reason:     'ticket_exists' | 'service_order_type_not_allowed' | null;
  /** Nur bei Reklamation: Task des Original-Austauschs und dessen R2-Tickets. */
  complaint_origin_task_id:    number | null;
  complaint_origin_ticket_ids: number[];
}

/** Alle Fälle zur Schadensnummer, aufsteigend nach Task-ID. Leer = kein Assona-Task. */
export async function listAssona24hCases(damageNumber: string): Promise<Assona24hCase[]> {
  const params = new URLSearchParams({ damage_number: damageNumber });
  const body = await request<Assona24hCase>(`${BASE}/assona24h/cases?${params.toString()}`, { method: 'GET' });
  return body.items ?? [];
}

/**
 * „Reparatur hinzufügen" (Assona24H): legt das Ticket für den gewählten Fall an, wie
 * es sonst automatisch beim Lieferschein-Schritt passiert. Abgelehnt mit
 * `assona_task_not_found`, `ticket_already_exists` (dann `ApiError.body.ticket_ids`)
 * oder `service_order_type_not_allowed`.
 */
export async function createAssona24h(damageNumber: string, taskId: number): Promise<TicketDetail> {
  const body = await request<TicketDetail>(`${BASE}/assona24h`, {
    method: 'POST',
    body:   JSON.stringify({ damage_number: damageNumber, task_id: taskId }),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Dashboard-Kennzahlen (Anzahl je Status, heute angelegt, gesamt). */
export async function stats(): Promise<TicketStats> {
  const body = await request<TicketStats>(`${BASE}/stats`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function refreshFmip(id: number): Promise<FmipResult> {
  const body = await request<FmipResult>(`${BASE}/${id}/fmip-refresh`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Liest nur Gespeichertes — die Schnittstelle wird dabei nicht aufgerufen. */
export async function getImeiInformation(id: number): Promise<ImeiInformation> {
  const body = await request<ImeiInformation>(`${BASE}/${id}/imei-information`, { method: 'GET' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/**
 * Fragt die Detailauskunft bei der Schnittstelle ab — kostenpflichtig. Nur auf eine
 * ausdrückliche Benutzeraktion hin aufrufen, nie beim Laden einer Ansicht.
 */
export async function fetchImeiInformation(id: number): Promise<ImeiInformation> {
  const body = await request<ImeiInformation>(`${BASE}/${id}/imei-information/fetch`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function printLabel(id: number): Promise<void> {
  await request(`${BASE}/${id}/print-label`, { method: 'POST' });
}

/**
 * Schließt das Ticket ab. Nur aus „Bericht erstellt" bzw. „Totalschaden" erlaubt —
 * der Server lehnt sonst mit CONFLICT ab (`status_not_closable` / `already_closed`).
 */
export async function closeTicket(id: number): Promise<StockBookingResult> {
  const body = await request<TicketDetail>(`${BASE}/${id}/process/abschliessen`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return { ticket: body.item, planned_shipment: plannedShipmentResult(body.planned_shipment) };
}

/** Ältere Antworten ohne das Feld gelten als „kein Versand vorgemerkt". */
function plannedShipmentResult(result?: PlannedShipmentResult): PlannedShipmentResult {
  return result ?? { attempted: false, created: false, printed: false, message: null };
}

export async function markEingelangt(id: number, payload: EingelangtPayload): Promise<EingelangtResult> {
  const body = await request<TicketDetail>(`${BASE}/${id}/process/eingelangt`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return { ticket: body.item };
}

/**
 * Fuehrt die offenen Lagerbuchungen zum Austauschgeraet aus (Lieferschein
 * abschliessen / Geraet ausbuchen). Was offen ist, entscheidet der Server.
 *
 * Eine fehlgeschlagene Buchung ist KEIN Fehler des Aufrufs — sie steht in
 * `stock_booking.error`. Nur echte Fehler (Konflikt, nicht gefunden) werfen.
 */
export async function bookSwapStock(id: number): Promise<{ state: SwapStockState; result: SwapStockBookingResult }> {
  const body = await request<SwapStockState>(`${BASE}/${id}/swap-stock/book`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return {
    state:  body.item,
    result: (body as { stock_booking?: SwapStockBookingResult }).stock_booking
      ?? { performed: [], skipped: [], failed: null, error: null },
  };
}

/**
 * Auswahlmenge für das eingegangene Gerät: Masterartikel, die in den Stammdaten
 * für Reparatur freigegeben sind. Die Liste ist kurz — das Modal lädt sie
 * komplett und filtert clientseitig.
 */
export async function listReceivedDeviceOptions(): Promise<ReceivedDeviceResult[]> {
  const body = await request<ReceivedDeviceResult>(`${DEVICES_BASE}/received-search`, { method: 'GET' });
  return body.items ?? [];
}

/** Lädt eine Datei (multipart) an ein Ticket hoch. Kein JSON-Content-Type setzen — Browser setzt boundary. */
export async function uploadFile(id: number, file: File, category: FileCategory, name?: string): Promise<TicketFile> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  if (name !== undefined) {
    form.append('name', name);
  }

  let resp: Response;
  try {
    resp = await fetch(`${BASE}/${id}/files`, {
      method:  'POST',
      headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
      body:    form,
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<TicketFile>;
  try {
    body = (await resp.json()) as ApiEnvelope<TicketFile>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS' || !body.item) {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result, body.fields);
  }
  return body.item;
}

/** Holt den Binärinhalt einer Datei MIT Bearer-Token (für Blob-Open im neuen Tab). */
export async function fetchFileBlob(ticketId: number, fileId: number): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(`${BASE}/${ticketId}/files/${fileId}`, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }
  if (!resp.ok) {
    throw new ApiError(`Datei nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}

/** Soft-Delete einer bereits hochgeladenen Datei. */
export async function deleteFile(ticketId: number, fileId: number): Promise<void> {
  await request(`${BASE}/${ticketId}/files/${fileId}`, { method: 'DELETE' });
}

/** Setzt/ändert das eingegangene Gerät des Tickets (historisiert serverseitig). */
/** Farben + Speichergrößen, die es zum Master wirklich als Artikel gibt. */
export async function listDeviceVariants(masterId: number): Promise<DeviceVariants> {
  const body = await request<DeviceColorVariant>(
    `${window.location.origin}/api/masterarticles/${masterId}/variants`,
    { method: 'GET' },
  );
  return { colors: body.items ?? [], provider_fallback: body.provider_fallback === true };
}

/** Auswählbare Gerätezustände („Grading"). */
export async function listDeviceConditions(): Promise<DeviceConditionRef[]> {
  const body = await request<DeviceConditionRef>(
    `${window.location.origin}/api/device-conditions`,
    { method: 'GET' },
  );
  return body.items ?? [];
}

/**
 * Servicepartner + Fehlerbilder samt Preis, für die es zu diesem Master eine
 * Rezeptur gibt. Leere Liste = zu diesem Master ist keine hinterlegt; dann wird
 * die Auswahl des geplanten Versands gar nicht angezeigt.
 */
/**
 * Auswahl für den geplanten Versand eines Altgeräts (ohne Master): alle aktiven
 * Servicepartner und Fehlerbilder der Stammdaten, ohne Preis — der wird händisch
 * eingegeben.
 */
export async function listLegacyServicepartnerOptions(): Promise<ServicepartnerOption[]> {
  const body = await request<ServicepartnerOption>(
    `${window.location.origin}/api/devices/legacy-servicepartner-options`,
    { method: 'GET' },
  );
  return body.items ?? [];
}

export async function listMasterServicepartnerOptions(masterId: number): Promise<ServicepartnerOption[]> {
  const body = await request<ServicepartnerOption>(
    `${window.location.origin}/api/masterarticles/${masterId}/servicepartner-options`,
    { method: 'GET' },
  );
  return body.items ?? [];
}

/**
 * Bucht ein bereits abgeschlossenes Ticket nachträglich ins Gerätelager ein.
 * Antwort ist das frische Ticket samt `stock_device`.
 */
export async function createStockDevice(id: number): Promise<StockBookingResult> {
  const body = await request<TicketDetail>(`${BASE}/${id}/stock-device`, { method: 'POST' });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return { ticket: body.item, planned_shipment: plannedShipmentResult(body.planned_shipment) };
}

/**
 * `deviceId` null + `alternativeDescription` gesetzt = Altgerät ohne Master; dann
 * sind Farbe und Speicher null und ein geplanter Versand braucht `plannedPrice`.
 */
export async function setTicketDevice(
  id: number,
  deviceId: number | null,
  colorId: number | null,
  memorySize: number | null,
  conditionId: number | null,
  plannedServicepartnerLocationId: number | null,
  plannedErrorPatternId: number | null,
  alternativeDescription: string | null = null,
  plannedPrice: number | null = null,
): Promise<TicketDetail> {
  const payload: Record<string, unknown> = {
    device_id:    deviceId,
    color_id:     colorId,
    memory_size:  memorySize,
    condition_id: conditionId,
    planned_servicepartner_location_id: plannedServicepartnerLocationId,
    planned_error_pattern_id:           plannedErrorPatternId,
    planned_price_net_eur:              plannedPrice,
  };
  // Nur beim Altgerät mitschicken — das Feld allein schaltet den Server auf „kein Master".
  if (alternativeDescription !== null) payload.alternative_device_description = alternativeDescription;
  const body = await request<TicketDetail>(`${BASE}/${id}/device`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}
